import * as React from 'react';
import {
  Alert,
  Button,
  FormGroup,
  InputGroup,
  InputGroupText,
  Label,
} from 'reactstrap';
import { useEffect, useState } from 'react';
import { Communicator } from '../../tools/Communication';
import { ContainerType } from '../ContainerComponents/ContainerPage';
import TryNewDesignBanner from './TryNewDesignBanner';
import { ResponsiveTable } from '../../components/Layout';
import {
  FaArrowDown,
  FaArrowUp,
  FaDownload,
  FaExclamationTriangle,
  FaLightbulb,
  FaStickyNote,
} from 'react-icons/fa';
import { randomId } from '@/utils/random';
import uploadToS3 from '@/utils/imageUpload';
import { useMutation, useQuery } from '@apollo/client';
import { requestUploadUrl } from '../../graphql/mutations';
import {
  expandNamesByLanguage,
  UserLanguage,
  IdByUserLanguage,
} from '@/utils/translation';
import { allUserCountriesQuery } from '../../graphql/queries';
import { MultiSelect } from '@mantine/core';
import QRCodeStyling from 'qr-code-styling';
import { download } from '@/utils/download';
import { ContentSection } from '../../components/Layout/ContentSection';
import ContainerTypeFilter from './ContainerTypeFilter';
import { useGetAllContainerTypeGroupsQuery } from '../../graphql/queries/getAllContainerTypeGroups.generated';
import SelectWithAddNew from '../../components/SelectWithAddNew';
import { distinct } from '@/utils/arrayUtils';
import CustomPagination from '../../components/CustomPagination';
import Fuse from 'fuse.js';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { ViewerContext } from '../../providers/ViewerContextProvider';
import i18n from '@/utils/i18n';
import Checkbox from '../../components/Checkbox';
import ContainerTypeDisplay from '../../components/ContainerType/ContainerTypeDisplay';
import { LayoutStoreInstance } from '../../LayoutStore';
import { useSyncContainerTypesToZohoMutation } from '../../graphql/mutations/syncContainerTypesToZoho.generated';
import { Role } from '../../contants';
import { ContainerTypeLabelGenerationModal } from './ContainerTypeLabelGenerationModal';

export const CONTAINER_TYPE_QR_CODE_PREFIX = 'HTTP://VYT.TO/CT/';

const PAGE_SIZE = 10;

const columns = [
  { id: 1, value: 'image', label: 'Image' },
  { id: 2, value: 'name', label: 'Name' },
  { id: 3, value: 'countries', label: 'Countries' },
  { id: 4, value: 'class', label: 'Class' },
  { id: 5, value: 'group', label: 'Group' },
  { id: 6, value: 'sizeHelper', label: 'Size Helper' },
  { id: 7, value: 'color', label: 'Container Box Color Hex Code' },
  { id: 8, value: 'packingUnit', label: 'Packing Unit' },
  { id: 9, value: 'sku', label: 'SKU' },
  { id: 10, value: 'branding', label: 'Branding' },
  { id: 11, value: 'legalOwner', label: 'Legal Owner' },
  { id: 12, value: 'packingInstructions', label: 'Packing Instructions' },
  { id: 13, value: 'weight', label: 'Weight' },
  { id: 14, value: 'archived', label: 'Archived' },
  { id: 15, value: 'segment', label: 'Segment' },
  { id: 16, value: 'cleanupNote', label: 'Cleanup Note' },
];

export default function EditContainerInfo() {
  const viewer = React.useContext(ViewerContext);
  const isAdministrator = viewer.role === Role.administrator;
  const isNoTAdministrator = viewer.role !== Role.administrator;

  const [allContainerTypes, setAllContainerTypes] = useState<ContainerType[]>(
    [],
  );

  const [allBranding, setAllBranding] = useState<string[]>([]);
  const [allLegalOwner, setAllLegalOwner] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setUpdating] = useState(false);
  const [language, setLanguage] = useState(UserLanguage.En);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [classFilter, setClassFilter] = useState<string[]>([]);
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [segmentFilter, setSegmentFilter] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [changedTypeIds, setChangedTypeIds] = useState<number[]>([]);
  const [columnFilter, setColumnFilter] = useState<string[]>(
    columns.map((c) => c.value),
  );
  const [containerClasses, setContainerClasses] = useState<
    { value: string; label: string }[]
  >([]);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveChangedContainerTypeIds, setArchiveChangedContainerTypeIds] =
    useState<number[]>([]);

  const [containerTypeForLabelModal, setContainerTypeForLabelModal] =
    useState<ContainerType | null>(null);

  const [requestUploadUrlQuery] = useMutation(requestUploadUrl);
  const { data: countriesData = { __type: { enumValues: [] } } } = useQuery(
    allUserCountriesQuery,
  );
  const countries: { value: string; label: string }[] =
    countriesData.__type.enumValues.map((country: any) => ({
      label: country.name,
      value: country.name,
    }));

  const [syncContainerTypesToZoho] = useSyncContainerTypesToZohoMutation({
    variables: {
      ids: archiveChangedContainerTypeIds,
    },
    onCompleted: (data) => {
      if (data.syncContainerTypesToZoho) {
        LayoutStoreInstance.setSuccess(
          'Container types synced to Zoho successfully',
        );
      } else {
        LayoutStoreInstance.setError('Container types syncing to Zoho failed');
      }
    },
    onError: (error: any) => {
      LayoutStoreInstance.setError(
        `Container types syncing to Zoho failed: ${error.message}`,
      );
    },
  });

  useEffect(() => {
    setAllBranding(
      allContainerTypes
        .map((ct) => ct.branding)
        .filter((b) => b != null)
        .filter(distinct),
    );
    setAllLegalOwner(
      allContainerTypes
        .map((ct) => ct.legalOwner)
        .filter((l) => l != null)
        .filter(distinct),
    );
  }, [allContainerTypes]);

  const filteredColumns = React.useMemo(
    () =>
      columns
        .filter((column) => columnFilter.includes(column.value))
        .sort((a, b) => a.id - b.id),
    [columnFilter],
  );

  const allSkus = React.useMemo(
    () =>
      allContainerTypes
        .map((ct) => ct.sku)
        .filter((sku) => !isNullOrWhitespace(sku)),
    [allContainerTypes],
  );

  const duplicateSkus = React.useMemo(
    () =>
      allSkus.filter(
        (sku, index, self) =>
          self.indexOf(sku) !== index && !isNullOrWhitespace(sku),
      ),
    [allSkus],
  );

  const filteredContainerTypes = React.useMemo(() => {
    let filtered = allContainerTypes;

    filtered = filtered.filter((f) => {
      return f.archived === showArchived;
    });

    if (searchTerm.length >= 3) {
      const fuse = new Fuse<ContainerType>(allContainerTypes, {
        keys: ['id', 'name', 'sku', 'branding', 'legalOwner'],
        includeScore: true,
        threshold: 0.3,
      });

      const results = fuse.search(searchTerm);
      filtered = results.map((result) => result.item);
    }

    if (!Number.isNaN(parseInt(searchTerm))) {
      const id = parseInt(searchTerm);
      filtered = filtered.filter((ct) => ct.id === id);
    }

    filtered = filtered
      .filter((ct) => {
        if (
          countryFilter.length > 0 &&
          !countryFilter.some((country) =>
            (ct.countryCodes || []).includes(country),
          )
        ) {
          return false;
        }

        if (classFilter.length > 0 && !classFilter.includes(ct.class)) {
          return false;
        }

        if (
          groupFilter.length > 0 &&
          !groupFilter.includes(ct.groupId.toString())
        ) {
          return false;
        }

        if (
          segmentFilter.length > 0 &&
          (!ct.segment || !segmentFilter.includes(ct.segment))
        ) {
          return false;
        }

        return true;
      })
      .sort((a, b) => b.orderKey - a.orderKey);

    setPageCount(Math.ceil(filtered.length / PAGE_SIZE));

    return filtered.slice(
      (currentPage - 1) * PAGE_SIZE,
      currentPage * PAGE_SIZE,
    );
  }, [
    allContainerTypes,
    countryFilter,
    classFilter,
    groupFilter,
    segmentFilter,
    currentPage,
    searchTerm,
    showArchived,
  ]);

  const columnFunctions: Record<string, any> = React.useMemo(
    () => ({
      id: (containerType: ContainerType) => (
        <span className="numbers-formatted">{containerType.id}</span>
      ),
      image: (containerType: ContainerType) => (
        <ContainerTypeDisplay
          containerType={containerType}
          showName={false}
          showContainerId={false}
          onClick={() => openFileSelector(containerType)}
        />
      ),
      name: (containerType: ContainerType) => (
        <div className="input-group">
          <input
            value={containerType.namesByLanguage[language]}
            title={containerType.namesByLanguage[language]}
            type="text"
            className="form-control"
            disabled={isNoTAdministrator}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'namesByLanguage',
                e.target.value,
              )
            }
          />
        </div>
      ),
      countries: (containerType: ContainerType) => (
        <div
          className="input-group"
          title={containerType.countryCodes?.join(', ') || ''}
        >
          <MultiSelect
            data={countries}
            searchable
            disabled={isNoTAdministrator}
            value={containerType.countryCodes}
            onChange={(value: string[]) =>
              updateContainerTypeCallback(
                containerType.id,
                'countryCodes',
                value,
              )
            }
          />
        </div>
      ),
      class: (containerType: ContainerType) => (
        <div className="input-group">
          <select
            className="form-control custom-select"
            disabled={isNoTAdministrator}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'class',
                e.target.value,
              )
            }
            value={containerType.class}
            title={containerType.class}
          >
            {containerClasses.map((cc) => (
              <option value={cc.value}>{cc.label}</option>
            ))}
          </select>
        </div>
      ),
      group: (containerType: ContainerType) => (
        <div className="input-group">
          {getContainerTypeGroupSelection(
            containerType.id,
            containerType.groupId,
          )}
        </div>
      ),
      sizeHelper: (containerType: ContainerType) => (
        <div className="input-group">
          <input
            style={{ width: '4rem' }}
            value={containerType.sizeHelper}
            title={containerType.sizeHelper}
            type="text"
            className="form-control"
            disabled={isNoTAdministrator}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'sizeHelper',
                e.target.value,
              )
            }
          />
        </div>
      ),
      color: (containerType: ContainerType) => (
        <div className="input-group" title={containerType.hexCode || ''}>
          <input
            type="text"
            className="form-control"
            disabled={isNoTAdministrator}
            value={containerType.hexCode || ''}
            onChange={(e) => {
              const hexValue = e.target.value
                .replace(/[^0-9A-Fa-f]/g, '')
                .slice(0, 6);
              updateContainerTypeCallback(
                containerType.id,
                'hexCode',
                hexValue,
              );
            }}
          />
          <input
            style={{ height: 'inherit' }}
            type="color"
            className="form-control"
            disabled={isNoTAdministrator}
            value={`#${containerType.hexCode}`}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'hexCode',
                e.target.value.slice(1),
              )
            }
          />
        </div>
      ),
      packingUnit: (containerType: ContainerType) => (
        <div className="input-group">
          <input
            style={{ width: '4rem' }}
            value={containerType.packingUnit}
            title={containerType.packingUnit.toString()}
            type="text"
            className="form-control"
            disabled={isNoTAdministrator}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'packingUnit',
                e.target.value.replace(/[^0-9]/g, ''),
              )
            }
          />
        </div>
      ),
      sku: (containerType: ContainerType) => (
        <div className="input-group">
          <input
            style={{ width: '6rem' }}
            value={containerType.sku}
            title={containerType.sku}
            type="text"
            className={
              'form-control ' +
              (allSkus.filter((sku) => sku === containerType.sku).length > 1
                ? 'is-invalid'
                : '')
            }
            disabled={isNoTAdministrator}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'sku',
                e.target.value.toUpperCase(),
              )
            }
          />
        </div>
      ),
      branding: (containerType: ContainerType) => (
        <div
          className="input-group"
          style={{ width: '6rem' }}
          title={containerType.branding}
        >
          <SelectWithAddNew
            options={allBranding}
            setOptions={(newBranding) =>
              setAllBranding([...allBranding, ...newBranding].filter(distinct))
            }
            disabled={isNoTAdministrator}
            selected={containerType.branding}
            setSelected={(newBranding) =>
              updateContainerTypeCallback(
                containerType.id,
                'branding',
                newBranding,
              )
            }
          />
        </div>
      ),
      legalOwner: (containerType: ContainerType) => (
        <div
          className="input-group"
          style={{ width: '6rem' }}
          title={containerType.legalOwner}
        >
          <SelectWithAddNew
            options={allLegalOwner}
            setOptions={(newLegalOwner) =>
              setAllLegalOwner(
                [...allLegalOwner, ...newLegalOwner].filter(distinct),
              )
            }
            disabled={isNoTAdministrator}
            selected={containerType.legalOwner}
            setSelected={(newLegalOwner) =>
              updateContainerTypeCallback(
                containerType.id,
                'legalOwner',
                newLegalOwner,
              )
            }
          />
        </div>
      ),
      packingInstructions: (containerType: ContainerType) => (
        <div className="input-group">
          <input
            style={{ width: '4rem' }}
            value={containerType.packingInstructions}
            title={containerType.packingInstructions}
            type="text"
            className="form-control"
            disabled={isNoTAdministrator}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'packingInstructions',
                e.target.value,
              )
            }
          />
        </div>
      ),
      cleanupNote: (containerType: ContainerType) => (
        <div className="input-group">
          <input
            style={{ width: '10rem' }}
            value={containerType.cleanupNote ?? ''}
            title={containerType.cleanupNote}
            type="text"
            className="form-control"
            placeholder="Keep / Archive / Merge with [id]"
            disabled={isNoTAdministrator}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'cleanupNote',
                e.target.value,
              )
            }
          />
        </div>
      ),
      weight: (containerType: ContainerType) => (
        <div className="input-group">
          <input
            style={{ width: '4rem' }}
            value={containerType.weight}
            title={containerType.weight.toString()}
            type="text"
            className="form-control"
            disabled={isNoTAdministrator}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'weight',
                e.target.value,
              )
            }
          />
        </div>
      ),
      archived: (containerType: ContainerType) => (
        <Checkbox
          checked={containerType.archived}
          onClick={() => {
            updateContainerTypeCallback(
              containerType.id,
              'archived',
              !containerType.archived,
            );
          }}
        />
      ),
      segment: (containerType: ContainerType) => (
        <div className="input-group">
          <select
            className="form-control custom-select"
            disabled={isNoTAdministrator}
            onChange={(e) =>
              updateContainerTypeCallback(
                containerType.id,
                'segment',
                e.target.value === '' ? null : e.target.value,
              )
            }
            value={containerType.segment || ''}
            title={containerType.segment || ''}
          >
            <option value="">{i18n.t('EditContainerInfo:segmentNone')}</option>
            <option value="Drinks">
              {i18n.t('EditContainerInfo:segmentDrinks')}
            </option>
            <option value="Food">
              {i18n.t('EditContainerInfo:segmentFood')}
            </option>
            <option value="Cutlery">
              {i18n.t('EditContainerInfo:segmentCutlery')}
            </option>
          </select>
        </div>
      ),
    }),
    [allBranding, allLegalOwner, language, countries],
  );

  const { data: containerTypeGroups, loading: loadingContainerTypeGroups } =
    useGetAllContainerTypeGroupsQuery({
      fetchPolicy: 'no-cache',
    });

  useEffect(() => {
    load();
  }, []);

  async function load() {
    const promises = [
      Communicator.Get('/Container/GetAllContainerCategories', {})
        .then((response) => response.json())
        .then((data: { containerTypes: ContainerType[] }) => {
          if (data.containerTypes) {
            // assume that container types have ids from 1 to n without 'holes'
            setAllContainerTypes(
              data.containerTypes
                .map((ct) => expandNamesByLanguage(ct) as ContainerType)
                .sort((a, b) => a.id - b.id),
            );
          }
        }),
      Communicator.Get('/Container/GetAllContainerClasses', {})
        .then((response) => response.json())
        .then((containerClasses: { id: string; name: string }[]) =>
          setContainerClasses(
            containerClasses.map((cc) => ({ value: cc.name, label: cc.name })),
          ),
        ),
    ];
    await Promise.all(promises);
    setIsLoading(false);
  }

  async function save() {
    if (isNoTAdministrator) {
      return;
    }

    setUpdating(true);
    try {
      const result = await Communicator.PostData(
        '/Container/UpdateContainerTypeInformation',
        allContainerTypes
          .filter((ct) => changedTypeIds.includes(ct.id))
          .map((ct) => {
            const namesByLanguage: { [key: number]: string } = {};

            for (const language of Object.keys(UserLanguage)) {
              namesByLanguage[IdByUserLanguage[language]] =
                ct.namesByLanguage[language];
            }

            return {
              ...ct,
              namesByLanguage,
            };
          }),
      );

      const res: ContainerType[] = await result.json();

      if (archiveChangedContainerTypeIds.length > 0) {
        // Sync container Types to Zoho when the archived state changed.
        syncContainerTypesToZoho();

        setArchiveChangedContainerTypeIds([]);
      }

      setAllContainerTypes(
        [...res].sort((a, b) => {
          if (a.orderKey !== b.orderKey) {
            return b.orderKey - a.orderKey;
          }
          return a.id - b.id;
        }),
      );

      setChangedTypeIds([]);
    } catch (e: any) {
      const body = await e.json();
      console.error(body);

      alert(`Error saving container types ${body.errors.join(', ')}`);
    }

    setUpdating(false);
  }

  function updateContainerType(
    containerTypeId: number,
    attrName: string,
    attrValue: any,
  ) {
    if (isNoTAdministrator) {
      return;
    }

    if (!changedTypeIds.includes(containerTypeId)) {
      setChangedTypeIds((oldIds) => [...oldIds, containerTypeId]);
    }
    setAllContainerTypes((oldContainerTypes) => {
      const newContainerTypes = [...oldContainerTypes];
      const containerType: any = newContainerTypes.find(
        (ct) => ct.id === containerTypeId,
      );

      if (containerType === null) {
        console.error(
          `Error updating container type. Could not find container type for id: ${containerTypeId}`,
        );

        return newContainerTypes;
      } else {
        if (attrName === 'archived') {
          const id = containerType?.id;
          setArchiveChangedContainerTypeIds((oldIds) => {
            if (!oldIds.includes(id)) {
              return [...oldIds, id];
            }
            return oldIds;
          });
        }

        if (attrName === 'namesByLanguage') {
          if (language === UserLanguage.En) {
            const differentLangs = Object.keys(
              containerType.namesByLanguage,
            ).filter(
              (l) =>
                containerType.namesByLanguage[l] !==
                containerType.namesByLanguage[UserLanguage.En],
            );
            const differentLangsWithValuesString = differentLangs
              .map((l) => `${l}: ${containerType.namesByLanguage[l]}`)
              .join(', ');
            if (
              differentLangs.length > 0 &&
              !confirm(
                `This will overwrite all other language names. Are you sure you want to overwrite custom values for ${differentLangsWithValuesString}?`,
              )
            ) {
              return oldContainerTypes;
            }
            Object.keys(containerType.namesByLanguage).forEach(
              (l) => (containerType.namesByLanguage[l] = attrValue),
            );
          } else {
            containerType.namesByLanguage[language] = attrValue;
          }
        } else {
          console.log(
            'Setting',
            attrName,
            'to',
            attrValue,
            'of',
            containerType,
          );

          containerType[attrName] = attrValue;
        }
      }
      return newContainerTypes;
    });
  }

  const updateContainerTypeCallback = React.useCallback(updateContainerType, [
    language,
  ]);

  function setOrderKey(containerTypeId: number, newOrderKey: number) {
    if (isNoTAdministrator) {
      return;
    }

    if (
      newOrderKey < 1 ||
      allContainerTypes.every((ct) => ct.orderKey > newOrderKey) ||
      allContainerTypes.every((ct) => ct.orderKey < newOrderKey)
    ) {
      console.log('NOOP');
      return;
    }
    setAllContainerTypes((oldContainerTypes) => {
      const newContainerTypes = [...oldContainerTypes];
      const containerType = newContainerTypes.find(
        (ct) => ct.id === containerTypeId,
      );
      if (containerType == null) {
        throw new Error(
          'Can not find container type with id: ' + containerTypeId,
        );
      }
      const other = newContainerTypes.find(
        (containerType) => containerType.orderKey === newOrderKey,
      );
      if (other != null) {
        other.orderKey = containerType.orderKey;
      }
      containerType.orderKey = newOrderKey;
      return newContainerTypes;
    });
  }

  function openFileSelector(containerType: ContainerType) {
    if (isNoTAdministrator) {
      return;
    }

    const input = window.document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => uploadImage(containerType, e);
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  async function uploadImage(containerType: ContainerType, e: any) {
    const { files } = e.target;
    const file = (files || [])[0];
    if (file) {
      const { name: filename, type: filetype } = file;
      let uniqueFilename = `${randomId(6)}_containerType_image_${
        containerType.id
      }`;
      const { data } = await requestUploadUrlQuery({
        variables: { filename: uniqueFilename, filetype },
      });
      if (data.requestUploadUrl) {
        const url = await uploadToS3(file, data.requestUploadUrl);
        if (url == null) {
          alert(`Error uploading image`);
        } else {
          updateContainerType(containerType.id, 'imageUrl', url);
        }
      }
    }
  }

  function getQrCodeStyling(id: number) {
    return new QRCodeStyling({
      width: 200,
      data: `${CONTAINER_TYPE_QR_CODE_PREFIX}${id}`,
      height: 200,
      dotsOptions: {
        color: '#000000',
        type: 'square',
      },
      imageOptions: {
        crossOrigin: 'anonymous',
      },
      backgroundOptions: {
        color: '#FFFFFF',
      },
      margin: 10,
      qrOptions: {
        errorCorrectionLevel: 'Q',
      },
    });
  }

  async function downloadQrCode(id: number) {
    const qrCode = getQrCodeStyling(id);
    const blob: any = await qrCode.getRawData('png');
    download(blob, `container_type_${id}_qr_code.png`);
  }

  if (isLoading || loadingContainerTypeGroups) {
    return <p>loading</p>;
  }

  function getContainerTypeGroupSelection(
    containerTypeId: number,
    groupId: number | string,
  ) {
    const containerTypeGroup =
      containerTypeGroups?.getAllContainerTypeGroups.find(
        (group) => group.id === parseInt(groupId as string, 10),
      );

    return (
      <select
        value={containerTypeGroup?.id}
        title={containerTypeGroup?.name || ''}
        className="form-control"
        disabled={isNoTAdministrator}
        onChange={(e) =>
          updateContainerType(
            containerTypeId,
            'groupId',
            parseInt(e.target.value, 10),
          )
        }
      >
        {containerTypeGroups?.getAllContainerTypeGroups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <ContentSection title="Edit Container Information" collapsable={false}>
      <TryNewDesignBanner />
      {isNoTAdministrator && (
        <Alert color="warning">
          <div className="d-flex">
            <div className="align-self-center">
              <FaExclamationTriangle />
            </div>
            <div className="keep-line-breaks ms-2">
              {i18n.t('EditContainerInfo:nonAdminWarning')}
            </div>
          </div>
        </Alert>
      )}

      <p className="keep-line-breaks">
        {i18n.t('EditContainerInfo:titleText')}
      </p>

      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('EditContainerInfo:languageSelection')}
        </span>

        <select
          className="form-control custom-select"
          onChange={(e) => setLanguage(e.target.value)}
          value={language}
        >
          {Object.keys(UserLanguage).map((k) => (
            <option value={k} label={k.toUpperCase()} />
          ))}
        </select>
      </div>

      <ContainerTypeFilter
        countryFilter={countryFilter}
        setCountryFilter={setCountryFilter}
        classFilter={classFilter}
        setClassFilter={setClassFilter}
        groupFilter={groupFilter}
        setGroupFilter={setGroupFilter}
        segmentFilter={segmentFilter}
        setSegmentFilter={setSegmentFilter}
        containerTypeGroups={
          containerTypeGroups?.getAllContainerTypeGroups.map((g) => ({
            value: g.id.toString(),
            label: g.name,
          })) || []
        }
        containerClasses={containerClasses}
        segments={[
          { value: 'Drinks', label: i18n.t('EditContainerInfo:segmentDrinks') },
          { value: 'Food', label: i18n.t('EditContainerInfo:segmentFood') },
          {
            value: 'Cutlery',
            label: i18n.t('EditContainerInfo:segmentCutlery'),
          },
        ]}
        countries={countries}
        currencies={[]}
        currencyFilter={[]}
        setCurrencyFilter={() => {}}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      <FormGroup>
        <Label for="columnFilter">Columns</Label>
        <MultiSelect
          id="countryFilter"
          data={columns}
          searchable
          value={columnFilter}
          onChange={(value) => setColumnFilter(value)}
        />
      </FormGroup>

      <FormGroup>
        <InputGroup>
          <InputGroupText>Show Archived</InputGroupText>
          <InputGroupText>
            <Checkbox
              checked={showArchived}
              onClick={() => {
                setShowArchived(!showArchived);
              }}
            />
          </InputGroupText>
        </InputGroup>
      </FormGroup>

      <ResponsiveTable>
        <thead>
          <tr>
            <th>Id</th>
            {filteredColumns.map((column) => (
              <th key={column.id}>
                {column.value === 'segment'
                  ? i18n.t('EditContainerInfo:segment')
                  : column.label}
              </th>
            ))}
            <th>OrderKey</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filteredContainerTypes.map((containerType) => {
            return (
              <tr key={containerType.id}>
                <td>{containerType.id}</td>
                {filteredColumns.map((column) => {
                  const columnFunction = columnFunctions[column.value];
                  return (
                    <td key={column.id}>{columnFunction(containerType)}</td>
                  );
                })}
                <td
                  className="numbers-formatted"
                  title={containerType.orderKey.toString()}
                >
                  {containerType.orderKey}
                </td>
                <td>
                  <div style={{ float: 'right', marginRight: '1em' }}>
                    <button
                      disabled={isNoTAdministrator}
                      onClick={(e) => {
                        setOrderKey(
                          containerType.id,
                          containerType.orderKey + 1,
                        );
                      }}
                      style={{
                        border: '#00000000',
                        background: '#00000000',
                        marginRight: '1em',
                      }}
                    >
                      <FaArrowUp />
                    </button>
                    <button
                      disabled={isNoTAdministrator}
                      onClick={(e) =>
                        setOrderKey(
                          containerType.id,
                          containerType.orderKey - 1,
                        )
                      }
                      style={{
                        border: '#00000000',
                        background: '#00000000',
                      }}
                    >
                      <FaArrowDown />
                    </button>
                  </div>
                </td>
                <td>
                  <button>
                    <FaDownload
                      style={{ cursor: 'pointer' }}
                      onClick={() => downloadQrCode(containerType.id)}
                    />
                  </button>
                  <button title="Container type label sticker">
                    <FaStickyNote
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setContainerTypeForLabelModal(containerType);
                      }}
                    />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </ResponsiveTable>

      <CustomPagination
        currentPage={currentPage}
        pageCount={pageCount}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
      />
      {duplicateSkus.length > 0 && (
        <Alert color="danger">
          <div className="d-flex">
            <div className="align-self-center">
              <FaLightbulb />
            </div>
            <div className="ms-2">
              {`Please check the following SKUs for duplicates: ${duplicateSkus}`}
            </div>
          </div>
        </Alert>
      )}

      {isAdministrator && (
        <Button
          style={{ margin: '0.5em' }}
          color="primary"
          size="sm"
          onClick={save}
          disabled={
            isUpdating ||
            duplicateSkus.length > 0 ||
            changedTypeIds.length === 0
          }
        >
          {isUpdating ? 'Saving...' : 'Save'}
        </Button>
      )}

      {containerTypeForLabelModal && (
        <ContainerTypeLabelGenerationModal
          containerType={containerTypeForLabelModal}
          onClose={() => setContainerTypeForLabelModal(null)}
        />
      )}
    </ContentSection>
  );
}
